import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Home,
  ArrowLeft,
  Activity,
  CreditCard,
  UtensilsCrossed,
  BookOpen,
  Compass,
  AlertTriangle,
} from 'lucide-react';

const POPULAR_LINKS = [
  {
    title: 'Live Train Status',
    desc: 'Real-time train running status & delays',
    path: '/live-train-status',
    icon: Activity,
    color: 'from-violet-500 to-indigo-600',
    badge: 'Real-time',
  },
  {
    title: 'PNR Status',
    desc: 'Check coach, berth & confirmation status',
    path: '/pnr-status',
    icon: CreditCard,
    color: 'from-orange-500 to-rose-600',
    badge: '1-Click',
  },
  {
    title: 'Food on Train',
    desc: 'Order hot restaurant meals to your seat',
    path: '/food-on-train',
    icon: UtensilsCrossed,
    color: 'from-emerald-500 to-teal-600',
    badge: 'IRCTC Approved',
  },
  {
    title: 'Travel Stories',
    desc: 'Scenic routes, food diaries & train guides',
    path: '/stories',
    icon: BookOpen,
    color: 'from-purple-500 to-fuchsia-600',
    badge: 'Guides',
  },
];

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-10 sm:py-14">
      <div className="max-w-3xl w-full">
        {/* Main Cohesive Card Container */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl p-6 sm:p-10 shadow-xl border border-slate-100/90 text-center space-y-8">
          {/* 404 Icon & Title Header */}
          <div className="space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-tr from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-500/25">
              <Compass className="w-8 h-8 sm:w-10 sm:h-10 animate-spin-slow" />
            </div>

            <div className="flex items-center justify-center gap-1.5 bg-rose-50 text-rose-600 px-3.5 py-1 rounded-full text-xs font-black uppercase tracking-wider mx-auto w-fit border border-rose-100">
              <AlertTriangle size={13} />
              <span>404 — Invalid Route</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
              Lost Your Station?
            </h1>

            <div className="space-y-1.5 max-w-lg mx-auto">
              <p className="text-base sm:text-lg font-bold text-slate-700">
                The page or URL you requested could not be found.
              </p>
              <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                The link you followed may be broken, removed, or the address was entered incorrectly.
              </p>
            </div>
          </div>

          {/* Primary Action Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-1">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm bg-slate-100 text-slate-700 hover:bg-slate-200 transition-all cursor-pointer"
            >
              <ArrowLeft size={16} /> Go Back
            </button>

            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-extrabold text-sm bg-gradient-to-r from-violet-600 to-indigo-600 text-white shadow-md shadow-violet-500/25 hover:opacity-95 transition-all"
            >
              <Home size={16} /> Return to Homepage
            </Link>
          </div>

          {/* Key Services Grid */}
          <div className="pt-6 border-t border-slate-100 text-left">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 mb-4 text-center">
              Or Explore Key TrainPanda Services:
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {POPULAR_LINKS.map((item) => {
                const IconComp = item.icon;
                return (
                  <Link
                    key={item.title}
                    to={item.path}
                    className="group bg-slate-50/80 hover:bg-violet-50/60 p-3.5 rounded-2xl border border-slate-200/60 hover:border-violet-300 transition-all flex items-center gap-3"
                  >
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center shrink-0 shadow-sm`}>
                      <IconComp size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-extrabold text-slate-800 group-hover:text-violet-700 transition-colors truncate block">
                        {item.title}
                      </span>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
